// Concurrently active control rooms are enforced capacity: the server refuses
// to start (or reopen) a room that would take the tenant past its effective
// allowance with 403 { errorCode: 'subscriptionLimitReached',
// limitType: 'concurrentControlRooms', used, limit, currentTier }. Rooms that
// are already open are never closed or interrupted by that refusal.
//
// On Premium, control-room hours are enforced capacity too: once the month's
// hours are used the same 403 comes back with
// limitType: 'controlRoomHoursPerMonth' (used / limit in hours). Enterprise
// hours never refuse.

export const CONCURRENT_ROOMS_LIMIT_TYPE = 'concurrentControlRooms'
export const CONTROL_ROOM_HOURS_LIMIT_TYPE = 'controlRoomHoursPerMonth'

// True for the concurrent-control-room refusal (pass an axios response).
export const isConcurrentRoomLimit = (res) =>
  res?.status === 403 &&
  res?.data?.errorCode === 'subscriptionLimitReached' &&
  res?.data?.limitType === CONCURRENT_ROOMS_LIMIT_TYPE

// True for either control-room refusal (concurrent rooms or Premium hours) —
// what the start / reopen dialogs check.
export const isControlRoomLimit = (res) =>
  res?.status === 403 &&
  res?.data?.errorCode === 'subscriptionLimitReached' &&
  [CONCURRENT_ROOMS_LIMIT_TYPE, CONTROL_ROOM_HOURS_LIMIT_TYPE].includes(
    res?.data?.limitType,
  )

const roomsText = (count) =>
  `${count} control room${Number(count) === 1 ? '' : 's'}`

// The customer-facing explanation, shared by the start / reopen dialogs and
// the global upgrade popup.
export const concurrentRoomLimitMessage = ({ used, limit } = {}) => {
  const allows =
    limit != null
      ? `Your plan allows ${roomsText(limit)} to be open at the same time`
      : 'Your plan’s limit on control rooms open at the same time has been reached'
  const openNow =
    used != null ? `, and ${used} ${Number(used) === 1 ? 'is' : 'are'} open now` : ''
  return `${allows}${openNow}. Close another control room first, or contact AmbuNet about more capacity.`
}

const firstOfNextMonth = () => {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth() + 1, 1).toLocaleDateString(
    'en-GB',
    { day: 'numeric', month: 'long' },
  )
}

// Premium: the month's control-room hours are used up.
export const controlRoomHoursLimitMessage = ({ limit } = {}) =>
  `${
    limit != null
      ? `Your plan's ${limit} control-room hours for this month have been used`
      : "Your plan's control-room hours for this month have been used"
  }, so no control room can be started or reopened until ${firstOfNextMonth()}. Closed control rooms can still be viewed. Contact AmbuNet to add hours or move to Enterprise.`

// Whichever control-room refusal `limitInfo` (the 403 payload) is.
export const controlRoomLimitCopy = (limitInfo) =>
  limitInfo?.limitType === CONTROL_ROOM_HOURS_LIMIT_TYPE
    ? {
        title: 'Control-room hours used',
        message: controlRoomHoursLimitMessage(limitInfo),
      }
    : {
        title: 'Control room capacity in use',
        message: concurrentRoomLimitMessage(limitInfo),
      }
